// Rebinding a canvas disposes its previous listeners and unfinished stroke.
const bindings = new WeakMap();

/**
 * Bind one drawing pointer. end runs once on release, cancellation, lost capture,
 * or blur. cleanup removes listeners/releases capture without calling end/save.
 * Callbacks receive the original event, including its clientX/clientY values.
 */
export function bindMaskPointerEvents(canvas, { start, move, end } = {}) {
  if (!canvas || typeof canvas.addEventListener !== 'function') {
    throw new TypeError('A canvas event target is required');
  }
  const previousBinding = bindings.get(canvas);
  if (previousBinding) previousBinding();
  const view = canvas.ownerDocument && canvas.ownerDocument.defaultView
    ? canvas.ownerDocument.defaultView
    : (typeof window !== 'undefined' ? window : null);
  const previousTouchAction = canvas.style ? canvas.style.touchAction : undefined;
  if (canvas.style) canvas.style.touchAction = 'none';
  const onStart = typeof start === 'function' ? start : () => {};
  const onMove = typeof move === 'function' ? move : () => {};
  const onEnd = typeof end === 'function' ? end : () => {};
  const listeners = [];
  let active = null;
  let disposed = false;

  function preventDefault(event) {
    if (event.cancelable !== false && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
  }

  function releaseCapture(pointerId) {
    if (typeof canvas.releasePointerCapture !== 'function') return;
    try {
      if (typeof canvas.hasPointerCapture !== 'function' || canvas.hasPointerCapture(pointerId)) {
        canvas.releasePointerCapture(pointerId);
      }
    } catch (_) {
      // A removed canvas or cancelled gesture may already have released capture.
    }
  }

  function finish(event, includeLastPoint) {
    if (disposed || !active) return;
    const stroke = active;
    // Clear first: release can emit lost capture, and pointerup can also bubble
    // from canvas to window. Neither must trigger another save.
    active = null;
    preventDefault(event);
    try {
      try {
        if (includeLastPoint && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)
          && (event.clientX !== stroke.x || event.clientY !== stroke.y)) {
          onMove(event);
        }
      } finally {
        if (!disposed) onEnd(event);
      }
    } finally {
      releaseCapture(stroke.id);
    }
  }

  function pointerDown(event) {
    if (disposed || active || event.isPrimary === false) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    preventDefault(event);
    active = { id: event.pointerId, x: event.clientX, y: event.clientY };
    try {
      if (typeof canvas.setPointerCapture === 'function') canvas.setPointerCapture(event.pointerId);
    } catch (_) {
      // Window pointerup/cancel is the fallback when capture is unavailable.
    }
    try {
      onStart(event);
    } catch (error) {
      const stroke = active;
      active = null;
      if (stroke) releaseCapture(stroke.id);
      throw error;
    }
  }

  function pointerMove(event) {
    if (disposed || !active || event.pointerId !== active.id) return;
    // Also handle a release outside the browser when no pointerup was delivered.
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      finish(event, false);
      return;
    }
    preventDefault(event);
    active.x = event.clientX;
    active.y = event.clientY;
    onMove(event);
  }

  function pointerUp(event) {
    if (!active || event.pointerId !== active.id) return;
    finish(event, true);
  }

  function pointerCancel(event) {
    if (!active || event.pointerId !== active.id) return;
    finish(event, false);
  }

  function blur(event) { finish(event, false); }

  function listen(target, type, handler) {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener(type, handler, { passive: false });
    listeners.push([target, type, handler]);
  }
  listen(canvas, 'pointerdown', pointerDown);
  listen(canvas, 'pointermove', pointerMove);
  listen(canvas, 'pointerup', pointerUp);
  listen(canvas, 'pointercancel', pointerCancel);
  listen(canvas, 'lostpointercapture', pointerCancel);
  listen(view, 'pointerup', pointerUp);
  listen(view, 'pointercancel', pointerCancel);
  listen(view, 'blur', blur);

  function cleanup() {
    if (disposed) return;
    disposed = true;
    const stroke = active;
    active = null;
    for (const [target, type, handler] of listeners) target.removeEventListener(type, handler);
    if (stroke) releaseCapture(stroke.id);
    if (canvas.style && canvas.style.touchAction === 'none') {
      canvas.style.touchAction = previousTouchAction;
    }
    if (bindings.get(canvas) === cleanup) bindings.delete(canvas);
  }
  bindings.set(canvas, cleanup);
  return cleanup;
}